import { useEffect, useState, type FC } from "react";
import { message } from "antd";

import { Modal } from "../../../shared";
import Payment from "../../NewAffiliate/Payment/Payment";
import type {
  PaymentStepValue,
  RevenueSource,
} from "../../NewAffiliate/Payment/Payment";
import { mapPaymentToPayload } from "../../../../queries/affiliates/affiliateMappers";
import { usePatchAffiliate } from "../../../../queries/affiliates/usePatchAffiliate";
import type { OperatorAffiliateDetail } from "../../../../types/affiliates";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import {
  getApiErrorMessage,
  getApiSuccessMessage,
} from "../../../../utils/ErrorUtils";

export type EditAffiliatePlanModalProps = {
  open: boolean;
  affiliate: OperatorAffiliateDetail;
  onClose: () => void;
};

const planToFormValue = (
  detail: OperatorAffiliateDetail,
): Partial<PaymentStepValue> => {
  const plan = detail.current_plan;
  if (!plan) return { type: "cpa" };

  const cpa = {
    actionType: plan.cpa_action_type ?? undefined,
    paymentPerAction: plan.cpa_payment_per_action ?? undefined,
  };
  if (plan.plan_type === "CPA") {
    return { type: "cpa", cpa };
  }

  const source: RevenueSource =
    plan.revenue_source === "sportsbook"
      ? "sportsbook"
      : plan.revenue_source === "sportsbook_casino"
        ? "sportsbook_casino"
        : "casino";
  const revShare = {
    revenueSource: source,
    sportsbook: {
      minProfit: plan.min_sportsbook_profit_threshold ?? undefined,
      revenuePct: plan.revenue_share_pct_sportsbook ?? undefined,
    },
    casino: {
      minProfit: plan.min_casino_profit_threshold ?? undefined,
      revenuePct: plan.revenue_share_pct_casino ?? undefined,
    },
  };

  // Hybrid carries BOTH components — hydrating it as rev_share (the old
  // fallback) silently dropped the CPA half on the next save.
  if (plan.plan_type === "HYBRID") {
    return { type: "hybrid", cpa, revShare };
  }
  return { type: "rev_share", revShare };
};

const EditAffiliatePlanModal: FC<EditAffiliatePlanModalProps> = ({
  open,
  affiliate,
  onClose,
}) => {
  const [values, setValues] = useState<Partial<PaymentStepValue>>(
    planToFormValue(affiliate),
  );
  const patch = usePatchAffiliate();

  useEffect(() => {
    if (open) setValues(planToFormValue(affiliate));
  }, [open, affiliate]);

  const handleSubmit = async () => {
    const plan = mapPaymentToPayload(values);
    if (!plan) {
      message.error(AppStrings.PAM_AFFILIATE_PLAN_INCOMPLETE);
      return;
    }
    try {
      const updated = await patch.mutateAsync({
        affiliateId: affiliate.affiliate_id,
        payload: { plan },
      });
      message.success(
        getApiSuccessMessage(updated, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED),
      );
      onClose();
    } catch (err) {
      message.error(getApiErrorMessage(err, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={AppStrings.PAM_AFFILIATE_EDIT_PLAN_MODAL_TITLE}
      size="xl"
      destroyOnHidden
      data-testid="edit-affiliate-plan-modal"
      primaryAction={{
        label: AppStrings.PAM_AFFILIATE_SAVE_PLAN,
        onClick: handleSubmit,
        loading: patch.isPending,
        disabled: patch.isPending,
      }}
      secondaryAction={{
        label: AppStrings.CANCEL,
        onClick: onClose,
      }}
    >
      {/* Modal footer owns Save/Cancel — hide the step's internal Continue. */}
      <Payment
        initialValues={values}
        onValuesChange={setValues}
        hideFooter
      />
    </Modal>
  );
};

export default EditAffiliatePlanModal;
